import { createSlice } from '@reduxjs/toolkit'; 
import axios from 'axios'; 

// Coalesce concurrent fetches per type
const pendingByType = {}; 
const lastSuccessAtByType = {}; 

const reviewsSlice = createSlice({ 
  name: 'reviews', 
  initialState: { 
    reviews: [], 
    loading: false, 
    error: null, 
  }, 
  reducers: { 
    fetchReviewsStart(state) { 
      state.loading = true; 
      state.error = null; 
    }, 
    fetchReviewsSuccess(state, action) { 
      state.loading = false; 
      state.reviews = action.payload; 
    }, 
    fetchReviewsFailure(state, action) { 
      state.loading = false; 
      state.error = action.payload; 
    }, 
  }, 
});

export const { 
  fetchReviewsStart, 
  fetchReviewsSuccess, 
  fetchReviewsFailure, 
} = reviewsSlice.actions;

export const fetchReviews = () => async (dispatch) => { 
  const qs = new URLSearchParams(window.location.search); 
  const type = qs.get('type') === 'private' ? 'private' : 'group'; 

  // Cooldown: if we just successfully fetched this type very recently, skip.
  const now = Date.now(); 
  const lastOk = lastSuccessAtByType[type] || 0; 
  if (now - lastOk < 1200) { 
    console.log(`[reviews] SKIP due to cooldown (type=${type}) delta=${now - lastOk}ms`); 
    return; 
  } 

  // If a fetch for this type is already running, wait for it and return.
  if (pendingByType[type]) { 
    console.log(`[reviews] coalesced (type=${type}) – waiting for in-flight request`); 
    try { 
      await pendingByType[type]; 
    } catch {} 
    return; 
  } 

  console.log(`[reviews] START (type=${type})`); 
  dispatch(fetchReviewsStart()); 

  const requestUrl = `https://script.google.com/macros/s/AKfycbztQMAAkp2CnOo1cXCav7Yfk0JtoMF5JPPEMG5TlebB_fpYEo5wegS6IjfiKaUgFRrl/exec?type=${type}&t=${Date.now()}`; 

  pendingByType[type] = (async () => { 
    try { 
      console.log(`[reviews] LIVE FETCH (type=${type})`); 
      const response = await axios.get(requestUrl); 
      console.log(`[reviews] NETWORK OK (type=${type}) status=${response.status}`); 

      let raw = response.data; 
      if (!Array.isArray(raw)) { 
        try { 
          raw = JSON.parse(raw); 
        } catch { 
          raw = []; 
        } 
      } 

      const rows = Array.isArray(raw) ? raw : []; 
      const data = rows.slice(2).filter((review) => [1, 2, 3, 4, 5].includes(review[4])); 

      // Date validation
      const isValidDate = (dateString) => { 
        const [day, month, year] = (dateString || '').split('/').map(Number); 
        if (!day || !month || !year || isNaN(day) || isNaN(month) || isNaN(year)) return false; 
        if (year < 1900 || year > 2099 || month < 1 || month > 12 || day < 1 || day > 31) return false; 
        const d = new Date(year, month - 1, day); 
        return d.getDate() === day && d.getMonth() === month - 1 && d.getFullYear() === year; 
      };

      const filteredData = data.filter((review) => isValidDate(review[3]));
      

      dispatch(fetchReviewsSuccess(filteredData)); 
      lastSuccessAtByType[type] = Date.now(); // <-- mark success for cooldown 
    } catch (err) { 
      console.error('[reviews] FETCH FAILED:', { 
        message: err?.message, 
        status: err?.response?.status, 
        url: requestUrl, 
      }); 
      dispatch(fetchReviewsFailure(err.message)); 
    } finally { 
      delete pendingByType[type]; 
      console.log(`[reviews] END (type=${type})`); 
    } 
  })();

  await pendingByType[type]; 
};

export default reviewsSlice.reducer;